import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Trash2 } from "lucide-react";
import {
  authService,
  Capability,
  User,
  UserType,
} from "../services/authService";
import { EmptyState, ErrorState, LoadingState } from "../components/PageState";

const ALL_USER_TYPE_LABELS: Record<UserType, string> = {
  admin: "System Administrator",
  director: "School Director/Owner",
  principal: "Principal",
  head_teacher: "Head Teacher",
  director_of_studies: "Director of Studies",
  head_of_department: "Head of Department",
  class_teacher: "Class Teacher",
  teacher: "Teacher",
  school_doctor: "School Doctor",
  hostel_matron: "Hostel Matron/Warden",
  hostel_custodian: "Hostel Custodian",
  kitchen_manager: "Kitchen Manager",
  cafeteria_manager: "Cafeteria Manager",
  sports_master: "Sports Master",
  uniform_manager: "Uniform Manager",
  inventory_manager: "Inventory Manager",
  school_counsellor: "School Counsellor",
  librarian: "Librarian",
  cfo: "Chief Finance Officer",
  bursar: "Bursar/Accountant",
  transport_manager: "Transport Manager",
  bus_driver: "Bus Driver",
  disciplinary_committee: "Disciplinary Committee",
  board_viewer: "Board Chair/Board Viewer",
  donor_viewer: "Donor Viewer",
  parent: "Parent/Guardian",
  student: "Student",
};
const ALL_USER_TYPES = Object.keys(ALL_USER_TYPE_LABELS) as UserType[];

function readableError(error: unknown, fallback: string) {
  const data = (error as { response?: { data?: Record<string, unknown> } })
    ?.response?.data;
  if (!data) return fallback;
  if (typeof data === "object") {
    return Object.entries(data)
      .map(([field, message]) =>
        Array.isArray(message)
          ? `${field}: ${message.join(" ")}`
          : `${field}: ${message}`,
      )
      .join(" ");
  }
  return fallback;
}

function CapabilityRow({ capability }: { capability: Capability }) {
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: ({
      user_type,
      enabled,
    }: {
      user_type: UserType;
      enabled: boolean;
    }) => authService.setRoleCapability(capability.id, user_type, enabled),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["capabilities"] }),
  });

  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-xs text-gray-500">
            {capability.slug}
          </div>
          <div className="font-semibold text-gray-900">
            {capability.name}
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {ALL_USER_TYPES.map((userType) => {
          const active = capability.default_user_types.includes(userType);
          return (
            <button
              key={userType}
              type="button"
              disabled={toggle.isPending}
              onClick={() =>
                toggle.mutate({ user_type: userType, enabled: !active })
              }
              className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                active
                  ? "border-primary-600 bg-primary-600 text-white"
                  : "border-gray-300 bg-white text-gray-600 hover:border-gray-400"
              }`}
              title={
                active
                  ? `Remove ${ALL_USER_TYPE_LABELS[userType]} from this capability's defaults`
                  : `Grant this capability to ${ALL_USER_TYPE_LABELS[userType]} by default`
              }
            >
              {ALL_USER_TYPE_LABELS[userType]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function UserGrantsPanel({ user }: { user: User }) {
  const queryClient = useQueryClient();
  const queryKey = ["user-capability-grants", user.id];
  const grants = useQuery({
    queryKey,
    queryFn: () => authService.getUserCapabilityGrants(user.id),
  });
  const capabilities = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => authService.getCapabilities(),
  });

  const setGrant = useMutation({
    mutationFn: ({
      capability,
      is_revoked,
      reason,
    }: {
      capability: number;
      is_revoked: boolean;
      reason: string;
    }) =>
      authService.setUserCapabilityGrant(
        user.id,
        capability,
        is_revoked,
        reason,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });
  const removeGrant = useMutation({
    mutationFn: (grantId: number) =>
      authService.removeUserCapabilityGrant(user.id, grantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const submitOverride = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const reason = String(form.get("reason") || "").trim();
    if (reason.length < 5) {
      window.alert("Give a reason of at least 5 characters.");
      return;
    }
    setGrant.mutate(
      {
        capability: Number(form.get("capability")),
        is_revoked: form.get("action") === "revoke",
        reason,
      },
      { onSuccess: () => event.currentTarget.reset() },
    );
  };

  if (grants.isLoading) return <LoadingState label="Loading overrides…" />;
  if (grants.isError)
    return (
      <ErrorState
        message="Could not load this user's capability overrides."
        retry={() => grants.refetch()}
      />
    );

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-gray-900">
          {user.full_name || user.username}
        </div>
        <div className="text-xs text-gray-500">
          {grants.data?.effective_capabilities.length || 0} effective
          capabilities ({ALL_USER_TYPE_LABELS[user.user_type]} role default,
          plus overrides below)
        </div>
      </div>

      {(grants.data?.grants.length || 0) === 0 ? (
        <EmptyState
          title="No overrides"
          message="This user currently only has their role's default capabilities."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  Capability
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  Action
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  Reason
                </th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">
                  By
                </th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {grants.data?.grants.map((grant) => (
                <tr key={grant.id}>
                  <td className="px-3 py-2 font-mono text-xs">
                    {grant.capability_slug}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        grant.is_revoked
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {grant.is_revoked ? "Revoked" : "Granted"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{grant.reason}</td>
                  <td className="px-3 py-2 text-gray-500">
                    {grant.granted_by_name}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeGrant.mutate(grant.id)}
                      disabled={removeGrant.isPending}
                      className="text-gray-400 hover:text-red-600"
                      title="Remove this override, reverting to the role default"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form
        onSubmit={submitOverride}
        className="space-y-3 rounded-lg border bg-gray-50 p-4"
      >
        <div className="text-sm font-semibold text-gray-900">
          Add an override
        </div>
        {setGrant.isError && (
          <p className="text-sm text-red-600">
            {readableError(
              setGrant.error,
              "Could not save this override. Please try again.",
            )}
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <select
            name="capability"
            required
            className="rounded-md border-gray-300 text-sm"
          >
            <option value="">Select a capability…</option>
            {capabilities.data?.map((capability) => (
              <option key={capability.id} value={capability.id}>
                {capability.name}
              </option>
            ))}
          </select>
          <select
            name="action"
            required
            className="rounded-md border-gray-300 text-sm"
          >
            <option value="grant">
              Grant (beyond their role default)
            </option>
            <option value="revoke">
              Revoke (remove a capability their role would carry)
            </option>
          </select>
        </div>
        <textarea
          name="reason"
          required
          minLength={5}
          placeholder="Why is this exception being made?"
          className="w-full rounded-md border-gray-300 text-sm"
          rows={2}
        />
        <button
          type="submit"
          disabled={setGrant.isPending}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {setGrant.isPending ? "Saving…" : "Save override"}
        </button>
      </form>
    </div>
  );
}

export default function Permissions() {
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const capabilities = useQuery({
    queryKey: ["capabilities"],
    queryFn: () => authService.getCapabilities(),
  });
  const users = useQuery({
    queryKey: ["managed-users"],
    queryFn: () => authService.getManagedUsers(),
  });

  const visibleUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return (users.data || [])
      .filter((user) =>
        `${user.full_name} ${user.username} ${user.email}`
          .toLowerCase()
          .includes(term),
      )
      .slice(0, 8);
  }, [search, users.data]);

  const groupedCapabilities = useMemo(() => {
    const groups: Record<string, Capability[]> = {};
    for (const capability of capabilities.data || []) {
      (groups[capability.module] ||= []).push(capability);
    }
    return groups;
  }, [capabilities.data]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ShieldCheck className="h-7 w-7 text-primary-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Capability permissions
          </h1>
          <p className="text-gray-600">
            Control who can initiate, review, approve, and post financial
            transactions -- by capability, not job title. Role defaults apply
            to everyone with that role; per-user overrides handle exceptions.
          </p>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)]">
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Role defaults
          </h2>
          {capabilities.isLoading && (
            <LoadingState label="Loading capabilities…" />
          )}
          {capabilities.isError && (
            <ErrorState
              message="Could not load capabilities."
              retry={() => capabilities.refetch()}
            />
          )}
          {Object.entries(groupedCapabilities).map(([module, caps]) => (
            <div key={module} className="space-y-3">
              <h3 className="text-sm font-semibold uppercase text-gray-500">
                {module}
              </h3>
              {caps.map((capability) => (
                <CapabilityRow key={capability.id} capability={capability} />
              ))}
            </div>
          ))}
        </div>

        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Per-user overrides
          </h2>
          <input
            type="text"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setSelectedUser(null);
            }}
            placeholder="Search for a user by name, username, or email…"
            className="w-full rounded-md border-gray-300 text-sm"
          />
          {visibleUsers.length > 0 && !selectedUser && (
            <div className="divide-y rounded-lg border bg-white">
              {visibleUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => setSelectedUser(user)}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <div className="font-medium text-gray-900">
                    {user.full_name || user.username}
                  </div>
                  <div className="text-xs text-gray-500">
                    {ALL_USER_TYPE_LABELS[user.user_type]} · {user.email}
                  </div>
                </button>
              ))}
            </div>
          )}
          {selectedUser && (
            <div className="rounded-lg border bg-white p-4">
              <button
                type="button"
                onClick={() => setSelectedUser(null)}
                className="mb-3 text-xs font-medium text-primary-600 hover:underline"
              >
                ← Choose a different user
              </button>
              <UserGrantsPanel user={selectedUser} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
